import React, { useEffect, useRef, useState } from 'react';

const AnimatedCounter = ({ end, duration = 2000, prefix = '', suffix = '', className = '' }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 900;

  useEffect(() => {
    // On mobile, show final value immediately
    if (isMobile) {
      const endValue = parseFloat(end.toString().replace(/[^0-9.]/g, ''));
      setCount(endValue);
      setHasAnimated(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            animateCount();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated, isMobile]);

  const animateCount = () => {
    const startTime = performance.now();
    const endValue = parseFloat(end.toString().replace(/[^0-9.]/g, ''));
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(easeOutQuart * endValue);
      
      setCount(currentCount);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(endValue);
      }
    };
    
    requestAnimationFrame(animate);
  };

  // Format the number (add commas, decimals, etc.)
  const formatNumber = (num) => {
    if (end.toString().includes('.')) {
      return num.toFixed(1);
    }
    return num.toLocaleString();
  };

  return (
    <span ref={ref} className={className}>
      {prefix}{formatNumber(count)}{suffix}
    </span>
  );
};

export default AnimatedCounter;
